import { AxiosError, AxiosRequestConfig } from 'axios';

import {
    apiErrorsToConsolidatedErrorObject,
    generateApiUnexpectedError,
} from '../utils/data-management';
import { Session } from '../../contexts/session';
import { getVoteApiObj } from './getVoteApiObj';
import { apiError } from '../types';
import { Vote } from '../../models/Vote';
import Logger from '../../utils/logger';

interface VoteData {
    pollId: string;
    optionId: string;
}

interface AnonymousVoteData extends VoteData {
    userId: string;
}

export class VoteApi {
    public static create = async (
        data: VoteData,
        session: Session,
    ): Promise<apiError | Vote> => {
        return VoteApi.createBase(data, {
            headers: {
                Authorization: `Bearer ${session.idToken}`,
            },
        });
    };

    public static createAnonymous = async (
        data: AnonymousVoteData,
    ): Promise<apiError | Vote> => {
        return VoteApi.createBase(data, {}, '/anonymous');
    };

    private static createBase = async (
        data: VoteData | AnonymousVoteData,
        config: AxiosRequestConfig = {},
        baseUrl: string = '',
    ): Promise<apiError | Vote> => {
        const apiObj = getVoteApiObj();

        try {
            const response = await apiObj.post(baseUrl, data, config);

            if (response.status === 200 && response.data) {
                return {
                    id: response.data.id,
                    pollId: response.data.pollId,
                    optionId: response.data.optionId,
                } as Vote;
            } else {
                throw new Error();
            }
        } catch (err: any) {
            let error: apiError;

            if (
                err &&
                typeof err === 'object' &&
                'response' in err &&
                (err as AxiosError).response?.status === 400 &&
                (err as AxiosError).response?.data instanceof Array &&
                (err as AxiosError).response?.data.length
            ) {
                error = apiErrorsToConsolidatedErrorObject(
                    (err as AxiosError).response?.data,
                );
            } else {
                error = generateApiUnexpectedError('casting vote');
            }

            // console.error(err.response);
            return error;
        }
    };

    public static getUserVotes = async (
        session: Session,
    ): Promise<Vote[] | undefined> => {
        const apiObj = getVoteApiObj();

        try {
            const response = await apiObj.get('', {
                headers: {
                    Authorization: `Bearer ${session.idToken}`,
                },
            });

            const votes: Vote[] = [];

            if (response.status === 200 && response.data) {
                // const votesCollection = JSON.parse(response.data);
                const votesCollection = response.data;

                if (
                    votesCollection &&
                    votesCollection.count &&
                    votesCollection.data &&
                    Array.isArray(votesCollection.data)
                ) {
                    votesCollection.data.forEach(
                        (voteRecord: { [key: string]: any }) => {
                            if (
                                voteRecord &&
                                'id' in voteRecord &&
                                'pollId' in voteRecord &&
                                'optionId' in voteRecord
                            ) {
                                votes.push({
                                    id: voteRecord.id,
                                    pollId: voteRecord.pollId,
                                    optionId: voteRecord.optionId,
                                });
                            }
                        },
                    );
                }
            }

            return votes;
        } catch (err: unknown) {
            Logger.error(JSON.stringify(err));
        }
    };

    public static getAnonymousUserVotes = async (
        userId: string,
    ): Promise<Vote[] | undefined> => {
        const apiObj = getVoteApiObj();

        try {
            const response = await apiObj.get('/anonymous', {
                params: {
                    userId,
                },
            });

            const votes: Vote[] = [];

            if (response.status === 200 && response.data) {
                // const votesCollection = JSON.parse(response.data);
                const votesCollection = response.data;

                if (
                    votesCollection &&
                    votesCollection.count &&
                    votesCollection.data &&
                    Array.isArray(votesCollection.data)
                ) {
                    votesCollection.data.forEach(
                        (voteRecord: { [key: string]: any }) => {
                            if (
                                voteRecord &&
                                'id' in voteRecord &&
                                'pollId' in voteRecord &&
                                'optionId' in voteRecord
                            ) {
                                votes.push({
                                    id: voteRecord.id,
                                    pollId: voteRecord.pollId,
                                    optionId: voteRecord.optionId,
                                });
                            }
                        },
                    );
                }
            }

            return votes;
        } catch (err: unknown) {
            Logger.error(JSON.stringify(err));
        }
    };
}
